import { useState } from "react";

import { format, subMonths } from "date-fns";
import { toast } from "react-toastify";

import { Checkbox, Input, Modal } from "../../../../components";
import { ModalSize } from "../../../../components/modal";
import { useFetch } from "../../../../hooks";
import { cx } from "../../../../utils";

interface IMarketingExportModal {
  visible: boolean;
  toggle: () => void;
}

const INPUT_DATE_FORMAT = "yyyy-MM-dd";

/**
 * Shortcuts for the registration-date range. These filter *when the user signed up* - they are
 * not the connexion windows, which are always all four and are columns rather than filters.
 */
const REGISTRATION_PRESETS: { label: string; months: number | null }[] = [
  { label: "All time", months: null },
  { label: "Last month", months: 1 },
  { label: "Last 3 months", months: 3 },
  { label: "Last 6 months", months: 6 },
  { label: "Last 12 months", months: 12 },
];

const presetRange = (months: number | null) => {
  if (months === null) return { from: "", to: "" };

  const now = new Date();

  return {
    from: format(subMonths(now, months), INPUT_DATE_FORMAT),
    to: format(now, INPUT_DATE_FORMAT),
  };
};

const buildQuery = (
  onlyConsented: boolean,
  registeredFrom: string,
  registeredTo: string
) => {
  const params = new URLSearchParams({ onlyConsented: String(onlyConsented) });

  if (registeredFrom) params.append("registeredFrom", registeredFrom);
  if (registeredTo) params.append("registeredTo", registeredTo);

  return params.toString();
};

export const MarketingExportModal: React.FC<IMarketingExportModal> = ({
  visible,
  toggle,
}) => {
  const [onlyConsented, setOnlyConsented] = useState<boolean>(true);
  const [registeredFrom, setRegisteredFrom] = useState<string>("");
  const [registeredTo, setRegisteredTo] = useState<string>("");
  const { apiCall: downloadCsv, loading: downloadLoading } = useFetch("post");

  const invalidRange = Boolean(
    registeredFrom && registeredTo && registeredFrom > registeredTo
  );

  /**
   * The UI flag (REACT_APP_ENABLE_MARKETING_EXPORT) and the backend flag (the
   * EnableMarketingExport row in AuthenticationSettings) are independent, so the button can be
   * visible while the endpoint is switched off. That case returns 404 and gets its own message,
   * since the fix is a settings change rather than a retry.
   */
  const onDownloadError = async (error: any) => {
    if (error?.response?.status === 404) {
      toast.error("Export is disabled. Enable it in the server settings first.");
      return;
    }

    // responseType "blob" applies to error responses too, so the body is a Blob rather than
    // JSON - handing it straight to toast would render "[object Blob]".
    const data = error?.response?.data;
    let message = typeof data === "string" ? data : "";

    if (data instanceof Blob) {
      try {
        message = await data.text();
      } catch {
        /* fall through to the generic message */
      }
    }

    toast.error(message || "Export failed. Please try again.");
  };

  // Deliberately no request on open or on filter change - the only call this modal makes is the
  // download itself, when the user asks for it.
  const onDownload = () => {
    downloadCsv(
      `Users/MarketingExport/Download?${buildQuery(
        onlyConsented,
        registeredFrom,
        registeredTo
      )}`,
      {},
      (blob) => {
        // The response is already a Blob because of responseType below - stringifying it
        // would produce "{}" instead of the file.
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");

        anchor.href = url;
        anchor.download = `porta-marketing-export-${format(
          new Date(),
          "yyyyMMdd-HHmmss"
        )}.csv`;
        anchor.click();

        URL.revokeObjectURL(url);
        toast.success("Export downloaded successfully!");
        toggle();
      },
      onDownloadError,
      // Must be a POST: useFetch forwards this as the axios config, and axios.get takes the
      // config in the argument slot the hook uses for the body, so it would be dropped.
      { responseType: "blob" }
    );
  };

  return (
    <Modal
      visible={visible}
      hide={toggle}
      title="Export users for marketing"
      modalSize={ModalSize.md}
      withFooter
      confirmBtnText="Download CSV"
      onConfirmClick={onDownload}
      loading={downloadLoading}
      disabled={invalidRange}
      blockOutsideClick
    >
      <Checkbox
        id="marketing-export-only-consented"
        label="Only users who accepted marketing consent"
        checked={onlyConsented}
        onChange={(e) => setOnlyConsented(e.target.checked)}
        wrapperClassName="mb-6"
      />

      <div className="mb-6">
        <p className="text-sm text-primary-mainText mb-3">Registration date</p>

        <div className="flex flex-wrap gap-2 mb-4">
          {REGISTRATION_PRESETS.map((preset) => {
            const range = presetRange(preset.months);
            const isActive =
              registeredFrom === range.from && registeredTo === range.to;

            return (
              <button
                key={preset.label}
                type="button"
                onClick={() => {
                  setRegisteredFrom(range.from);
                  setRegisteredTo(range.to);
                }}
                className={cx([
                  "py-1.5 px-3 text-sm rounded border transition",
                  "focus:outline-none focus:shadow-outlinePrimary",
                  isActive
                    ? "border-primary text-primary"
                    : "border-primary-stroke text-primary-secText hover:text-primary",
                ])}
              >
                {preset.label}
              </button>
            );
          })}
        </div>

        <div className="flex gap-4">
          <Input
            name="registeredFrom"
            type="date"
            value={registeredFrom}
            onChange={(e) => setRegisteredFrom(e.target.value)}
            labelProps={{ children: "From" }}
            containerClassname="w-full"
            max={registeredTo || undefined}
          />

          <Input
            name="registeredTo"
            type="date"
            value={registeredTo}
            onChange={(e) => setRegisteredTo(e.target.value)}
            labelProps={{ children: "To" }}
            containerClassname="w-full"
            min={registeredFrom || undefined}
            error={
              invalidRange ? "Must be on or after the start date" : undefined
            }
          />
        </div>
      </div>

      {/* The retention cut-off and the fuller consent caveat both travel in the CSV's first-line
          comment, so they are not repeated here. This one line stays because it is the only
          caveat that should change what the admin does with the file. */}
      <p className="text-sm text-primary-secText">
        Not a send list — consent is from registration only and withdrawals
        aren't tracked.
      </p>
    </Modal>
  );
};
